// National Rail trains from Network Rail's train describer (TD) feed.
//
// The server streams berth changes (src/server/td, /nr/td/stream), each train
// tagged with the service it is (src/server/nr). public/nr-berths.json places
// berths on the track (scripts/build-berths.ts): a berth's point is the signal
// where it ends, and each observed step has the track path between two berths
// and how long trains take along it. So a train that steps from berth A into
// berth B runs along the A>B path, taking as long as trains usually do (never
// less than at line speed), then holds at B's signal until it steps again.
//
// Motion never jumps: a new step starts from wherever the train is drawn. A
// train only appears or disappears by fading.

import type { GeoJSONSourceSpecification, LayerSpecification } from 'maplibre-gl';
import type { Drawn } from '@tol/core/glitch';
import type { Canvas2D } from '@tol/core/render';
import { decodePolyline, dist } from '@tol/core/core';
import { inBox } from '@tol/core/onscreen';
import type { Box } from '@tol/core/onscreen';
import type { LatLngTuple } from '@tol/core/types';
import { NR_LINES } from '../shared/nr-lines.ts';

// Lines drawn (src/shared/nr-lines.ts: the server counts them for /quality).
export { NR_LINES };

export const NR_CREDIT =
  'Contains Network Rail data/materials © 2016 and <a href="https://www.gov.uk/government/publications/national-public-transport-access-node-schema">NaPTAN</a> public sector information licensed under <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/">OGL v3</a>';

// Seconds a step takes when the berth table has no timing for it.
const DEFAULT_STEP_S = 20;
// A train further than this from where its next step starts is re-placed
// (fading) rather than run there: the data put it somewhere else entirely.
const REPLACE_M = 2000;
const FADE_MS = 600;
const DEG = Math.PI / 180;
// Meters per degree of latitude.
const M_PER_DEG = 111320;
// A step with no recorded path is run in a straight line only this short;
// longer, and the berths are more likely wrong than the track straight.
const STRAIGHT_MAX_M = 800;
// ms a train in a berth with no position stays drawn where it was. It can
// run on for kilometers through berths we can't place (seen: 31 km in VC),
// so after this it fades out, and fades in at the next placed berth.
const LOST_MS = 30_000;
// ms a train stays its home area's (see NrTrain.moved).
const HOME_MS = 20_000;
// m: a train reported by another area within this of one on the map, with
// the same headcode (and not another timetable uid), is that train.
const TWIN_M = 5000;
// ms: a train no area holds any more stays this long, for the area's next
// report of it (WI clears 0167 a second before stepping A155 on to 0171).
const GRACE_MS = 3000;
// The server sends a heartbeat every 15 s. Three missed heartbeats make the
// stream stale; its trains fade, then are forgotten unless a snapshot restores
// them after EventSource reconnects.
const STREAM_SILENCE_MS = 45_000;
const STREAM_CLEAR_MS = 30_000;
// m: two ends of a step in different areas within this are the same place
// (the berth builder treats copies further apart as disagreeing), so an
// untimed step may borrow the other's recorded time.
const SAME_M = 300;
// m/s: the fastest a train is drawn going (about 112 mph).
const MAX_SPEED = 50;

interface Berth {
  readonly lat: number;
  readonly lon: number;
  // Compass degrees (clockwise from north) trains pass the signal in, if known.
  readonly heading?: number;
}

interface Edge {
  readonly path: readonly LatLngTuple[];
  readonly seconds: number;
  // Whether seconds are from recorded steps (not the default or line speed).
  readonly timed?: boolean;
}

interface Area {
  readonly berths: ReadonlyMap<string, Berth>;
  readonly edges: ReadonlyMap<string, Edge>;
  // "from>into" (a step from a placed berth into one with no position) ->
  // the placed berth trains usually reach next, and the way there.
  readonly through?: ReadonlyMap<string, { readonly to: string; readonly edge: Edge }>;
}

export interface NrTag {
  readonly status: string;
  readonly lineId: string | null;
  readonly destination: string | null;
  readonly delay: number | null;
  // The timetable's train uid, when matched.
  readonly uid: string | null;
  readonly runDate?: string | null;
}

// A path with cumulative meters, for placing a point a fraction along it.
interface Track {
  readonly pts: readonly LatLngTuple[];
  readonly cum: readonly number[];
  readonly len: number;
}

interface NrTrain {
  descr: string;
  area: string;
  berth: string;
  tag: NrTag | null;
  track: Track | null;
  // Clock ms when the current track began, and its duration in ms.
  start: number;
  // Clock ms of the last report that moved it. `area` is the area that did:
  // its home, whose reports move it; another area's only do once home has
  // been quiet for HOME_MS (a hand-over).
  moved: number;
  duration: number;
  alpha: number;
  dead: boolean;
  // Where it was last drawn (container pixels), for picking.
  screen: { readonly x: number; readonly y: number } | null;
  // The heading of the signal it's held at: a train placed at a berth (on
  // load, or interposed) has no track to take a direction from.
  heading: number | null;
  // When it stepped into a berth with no position (clock ms), or null. It
  // stays where it was drawn for LOST_MS, then fades out until it reaches a
  // placed berth again.
  lost: number | null;
  // Clock ms when no area held it any more (it goes GRACE_MS later unless
  // one picks it up again), or null.
  gone: number | null;
}

type Json = Readonly<Record<string, unknown>>;
const isRecord = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);
const toLatLon = ([lat, lon]: LatLngTuple): Berth => ({ lat, lon });

function trackOf(pts: readonly LatLngTuple[]): Track {
  const cum = [0];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (a && b) total += dist(toLatLon(a), toLatLon(b));
    cum.push(total);
  }
  return { pts, cum, len: total };
}

// Point a fraction f along a track.
function pointOn(track: Track, f: number): LatLngTuple {
  const [first] = track.pts;
  const last = track.pts.at(-1);
  if (!first || !last) return [0, 0];
  if (track.len <= 0 || f <= 0) return first;
  if (f >= 1) return last;
  const s = f * track.len;
  let i = 1;
  while (i < track.cum.length - 1 && (track.cum[i] ?? 0) < s) i++;
  const a = track.pts[i - 1] ?? first;
  const b = track.pts[i] ?? last;
  const s0 = track.cum[i - 1] ?? 0;
  const s1 = track.cum[i] ?? s0;
  const u = s1 > s0 ? (s - s0) / (s1 - s0) : 1;
  return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
}

// The track's points beyond a fraction f along it: what's left to travel.
function restOf(track: Track, f: number): LatLngTuple[] {
  const s = f * track.len;
  return track.pts.filter((_, i) => (track.cum[i] ?? 0) > s + 0.01);
}

// A train pill centered on (x, y) pointing along ang, like the TfL trains
// (render.ts): tapered nose and a chevron for direction when the heading is
// known, a plain pill otherwise.
function pill(
  ctx: Canvas2D,
  x: number,
  y: number,
  ang: number | null,
  alpha: number,
  color: string,
  len: number,
  wid: number,
  s: number,
  ink: string,
): void {
  const r = wid / 2;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(x, y);
  ctx.rotate(ang ?? 0);
  ctx.beginPath();
  const back = -len / 2 + r;
  const front = len / 2 - r;
  ctx.moveTo(back, -r);
  ctx.lineTo(front, -r);
  ctx.arc(front, 0, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(back, r);
  ctx.arc(back, 0, r, Math.PI / 2, (3 * Math.PI) / 2);
  ctx.closePath();
  ctx.lineWidth = 3.2;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = ink;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fill();
  if (ang !== null) {
    const cw = wid * 0.27;
    const cl = len * 0.12;
    ctx.beginPath();
    ctx.moveTo(-cl, -cw);
    ctx.lineTo(cl, 0);
    ctx.lineTo(-cl, cw);
    ctx.lineWidth = Math.max(1.2, 1.35 * s);
    ctx.lineCap = 'round';
    ctx.strokeStyle = chevronColor(color, ink);
    ctx.stroke();
  }
  ctx.restore();
}

// Dark chevron on pale pills, white otherwise (as render.ts).
function chevronColor(hex: string, ink: string): string {
  const n = parseInt(hex.slice(1), 16);
  const y = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return y > 0.62 ? ink : '#FFFFFF';
}

// public/nr-berths.json -> positioned berths and timed paths, per TD area.
export function parseBerths(data: unknown): ReadonlyMap<string, Area> {
  const out = new Map<string, Area>();
  // The mutable edge maps, for the timing pass below (Area.edges is readonly).
  const edgeMaps: { readonly berths: ReadonlyMap<string, Berth>; readonly edges: Map<string, Edge> }[] = [];
  const areas = isRecord(data) ? data['areas'] : undefined;
  if (!isRecord(areas)) return out;
  for (const [areaId, a] of Object.entries(areas)) {
    if (!isRecord(a) || !isRecord(a['berths']) || !isRecord(a['edges'])) continue;
    const berths = new Map<string, Berth>();
    for (const [id, b] of Object.entries(a['berths'])) {
      if (!isRecord(b)) continue;
      const lat = num(b['lat']);
      const lon = num(b['lon']);
      const heading = num(b['heading']);
      if (lat !== undefined && lon !== undefined)
        berths.set(id, heading === undefined ? { lat, lon } : { lat, lon, heading });
    }
    const edges = new Map<string, Edge>();
    for (const [id, e] of Object.entries(a['edges'])) {
      if (!isRecord(e)) continue;
      const [from = '', to = ''] = id.split('>');
      const p = berths.get(from);
      const q = berths.get(to);
      const encoded = str(e['path']);
      const straight: readonly LatLngTuple[] | null =
        p && q && dist(p, q) <= STRAIGHT_MAX_M
          ? [
              [p.lat, p.lon],
              [q.lat, q.lon],
            ]
          : null;
      const path = encoded === undefined ? straight : decodePolyline(encoded);
      if (!path || path.length < 2) continue;
      const line = num(e['line']) ?? 0;
      const recorded = num(e['run']) ?? num(e['sec']);
      const usual = recorded ?? DEFAULT_STEP_S;
      // Never quicker than MAX_SPEED along the path: long steps often have
      // no line-speed time, and their recorded time (or the default) can be
      // far too short (Wimbledon's B551>B553: 4.85 km in 20 s).
      edges.set(id, {
        path,
        seconds: Math.max(usual, line, pathMeters(path) / MAX_SPEED, 1),
        timed: recorded !== undefined,
      });
    }
    const through = new Map<string, { readonly to: string; readonly edge: Edge }>();
    const via = a['through'];
    if (isRecord(via)) {
      for (const [id, e] of Object.entries(via)) {
        const to = isRecord(e) ? str(e['to']) : undefined;
        const encoded = isRecord(e) ? str(e['path']) : undefined;
        // Another area's berth (AREA:BERTH) is checked when used.
        if (!isRecord(e) || to === undefined || encoded === undefined || (!to.includes(':') && !berths.has(to)))
          continue;
        const path = decodePolyline(encoded);
        if (path.length < 2) continue;
        const seconds = Math.max(num(e['sec']) ?? DEFAULT_STEP_S, pathMeters(path) / MAX_SPEED, 1);
        through.set(id, { to, edge: { path, seconds } });
      }
    }
    out.set(areaId, { berths, edges, through });
    edgeMaps.push({ berths, edges });
  }
  // Areas overlap and report the same steps; one may have timed a step the
  // other never saw run (X3's 0363>0365 was run at line speed, a dash, while
  // X2 had it at 36 s). An untimed step takes the slowest recorded time of
  // the same step elsewhere, but only where both ends are at the same place:
  // berth ids repeat across areas that never meet (WI's 0417 is 47 km from
  // TE's), so timings from elsewhere must match both endpoints (within
  // SAME_M) to be shared.
  const timed = new Map<string, { readonly seconds: number; readonly from: Berth; readonly to: Berth }[]>();
  for (const area of edgeMaps) {
    for (const [id, e] of area.edges) {
      if (e.timed !== true) continue;
      const [from = '', to = ''] = id.split('>');
      const p = area.berths.get(from);
      const q = area.berths.get(to);
      if (!p || !q) continue;
      timed.set(id, [...(timed.get(id) ?? []), { seconds: e.seconds, from: p, to: q }]);
    }
  }
  for (const area of edgeMaps) {
    for (const [id, e] of area.edges) {
      if (e.timed === true) continue;
      const [from = '', to = ''] = id.split('>');
      const p = area.berths.get(from);
      const q = area.berths.get(to);
      if (!p || !q) continue;
      let best = e.seconds;
      for (const t of timed.get(id) ?? [])
        if (dist(p, t.from) <= SAME_M && dist(q, t.to) <= SAME_M && t.seconds > best) best = t.seconds;
      if (best > e.seconds) area.edges.set(id, { ...e, seconds: best, timed: true });
    }
  }
  return out;
}

export type NrCoverageBox = readonly [west: number, south: number, east: number, north: number];

// Small boxes along National Rail's recorded paths, used by the world page
// to open the stream only where one of these trains can be seen.
export function nrCoverageBoxes(data: unknown): NrCoverageBox[] {
  const boxes: NrCoverageBox[] = [];
  function addPath(path: readonly LatLngTuple[]): void {
    const pointsPerBox = 16;
    for (let i = 0; i < path.length - 1; i += pointsPerBox) {
      const points = path.slice(i, i + pointsPerBox + 1);
      boxes.push([
        Math.min(...points.map((p) => p[1])),
        Math.min(...points.map((p) => p[0])),
        Math.max(...points.map((p) => p[1])),
        Math.max(...points.map((p) => p[0])),
      ]);
    }
  }
  for (const area of parseBerths(data).values()) {
    for (const berth of area.berths.values()) boxes.push([berth.lon, berth.lat, berth.lon, berth.lat]);
    for (const edge of area.edges.values()) addPath(edge.path);
    for (const step of area.through?.values() ?? []) addPath(step.edge.path);
  }
  for (const edge of parseHandovers(data).values()) addPath(edge.path);
  return boxes;
}

// The berth table's hand-overs between areas ("A:berth>B:berth"): the way
// trains pass from one area's berth to the next area's, timed like edges.
export function parseHandovers(data: unknown): ReadonlyMap<string, Edge> {
  const out = new Map<string, Edge>();
  const table = isRecord(data) ? data['handovers'] : undefined;
  if (!isRecord(table)) return out;
  for (const [id, e] of Object.entries(table)) {
    const encoded = isRecord(e) ? str(e['path']) : undefined;
    if (!isRecord(e) || encoded === undefined) continue;
    const path = decodePolyline(encoded);
    if (path.length < 2) continue;
    out.set(id, { path, seconds: Math.max(num(e['sec']) ?? DEFAULT_STEP_S, pathMeters(path) / MAX_SPEED, 1) });
  }
  return out;
}

function pathMeters(path: readonly LatLngTuple[]): number {
  let length = 0;
  for (let i = 1; i < path.length; i++) {
    const u = path[i - 1];
    const v = path[i];
    if (u && v) length += dist({ lat: u[0], lon: u[1] }, { lat: v[0], lon: v[1] });
  }
  return length;
}

function parseTag(v: unknown): NrTag | null {
  if (!isRecord(v)) return null;
  return {
    status: str(v['status']) ?? 'unmatched',
    lineId: str(v['lineId']) ?? null,
    destination: str(v['destination']) ?? null,
    delay: num(v['delay']) ?? null,
    uid: str(v['uid']) ?? null,
    runDate: str(v['runDate']) ?? null,
  };
}

// The data of a stream event, or null if it isn't JSON.
function parse({ data }: { readonly data: unknown }): unknown {
  try {
    return typeof data === 'string' ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

// A train's color, if it's a matched train on a line we draw.
function shown(tag: NrTag | null): string | null {
  if (tag?.status !== 'matched' || tag.lineId === null || tag.lineId === 'eurostar') return null;
  return NR_LINES[tag.lineId]?.show ?? null;
}

// What a card needs about one train.
export interface NrTrainInfo {
  readonly key: string;
  readonly descr: string;
  readonly area: string;
  readonly berth: string;
  readonly tag: NrTag | null;
  readonly lat: number;
  readonly lon: number;
  // How opaque it's drawn, 0-1 (fading in or out).
  readonly alpha: number;
}

// What /nr/trains says about one train, for its card.
export interface NrDetails {
  readonly operator: string | null;
  readonly origin: string | null;
  readonly destination: string | null;
  // Minutes late (negative: early), or null without a TRUST report.
  readonly delay: number | null;
  readonly lastPlace: string | null;
  // Calls ahead (expected times), and made (timetabled departures).
  readonly next: readonly NrCall[];
  readonly past: readonly NrCall[];
}
export interface NrCall {
  readonly name: string;
  readonly time: number | null;
  readonly platform: string | null;
}

// /nr/trains calls, with the time `timeOf` picks.
function parseCalls(v: unknown, timeOf: (c: Readonly<Record<string, unknown>>) => number | null): NrCall[] {
  const list: readonly unknown[] = Array.isArray(v) ? v : [];
  const out: NrCall[] = [];
  for (const c of list) {
    if (!isRecord(c)) continue;
    const name = str(c['name']);
    if (name !== undefined) out.push({ name, time: timeOf(c), platform: str(c['platform']) ?? null });
  }
  return out;
}

const nameOf = (v: unknown): string | null => (isRecord(v) ? (str(v['name']) ?? null) : null);

// The /nr/trains entry for the train in this berth (by area and berth, the
// headcode as a check).
export function parseDetails(data: unknown, area: string, berth: string, descr: string): NrDetails | null {
  const list = isRecord(data) ? data['trains'] : undefined;
  if (!Array.isArray(list)) return null;
  const rows: readonly unknown[] = list;
  const row = rows.find((r) => isRecord(r) && r['area'] === area && r['berth'] === berth && r['headcode'] === descr);
  const service = isRecord(row) ? row['service'] : undefined;
  if (!isRecord(service)) return null;
  const last = service['lastReport'];
  const next = parseCalls(
    service['next'],
    (c) => num(c['expArr']) ?? num(c['arr']) ?? num(c['expDep']) ?? num(c['dep']) ?? null,
  );
  const past = parseCalls(service['past'], (c) => num(c['dep']) ?? num(c['arr']) ?? null);
  return {
    operator: nameOf(service['operator']),
    origin: nameOf(service['origin']),
    destination: nameOf(service['destination']),
    delay: num(service['delay']) ?? null,
    lastPlace: isRecord(last) ? nameOf(last['place']) : null,
    next,
    past,
  };
}

export interface NationalRail {
  // Draw the trains onto the train canvas. dt: ms since the last frame's draw
  // (0 for a camera-only redraw); scale: the renderer's train scale.
  // width, height: the canvas in CSS pixels.
  readonly draw: (ctx: Canvas2D, dt: number, scale: number, width: number, height: number) => void;
  // Trains shown per line id (hidden lines included), only those in the
  // box when given one.
  readonly counts: (within?: Box) => ReadonlyMap<string, number>;
  // The nearest drawn train within radius px of a container point, and how
  // far it is.
  readonly pick: (
    x: number,
    y: number,
    radius: number,
  ) => { readonly train: NrTrainInfo; readonly distance: number } | null;
  readonly info: (key: string) => NrTrainInfo | null;
  // The key of the drawn train running as this timetable uid, if any.
  readonly byUid: (uid: string) => string | null;
  readonly ownsRun: (uid: string, date: string, headcode: string) => boolean;
  // The trains the last draw put on screen.
  readonly drawn: () => readonly Drawn[];
  // Closes the stream and forgets every train (the world map, leaving London).
  readonly close: () => void;
}

// The parts of a MapLibre map this uses (a stand-in in tests).
export interface NrMap {
  readonly addSource: (id: string, source: GeoJSONSourceSpecification) => unknown;
  readonly addLayer: (layer: LayerSpecification, before?: string) => unknown;
  readonly project: (lngLat: readonly [number, number]) => { readonly x: number; readonly y: number };
}

// The TD stream: an EventSource, or a stand-in in tests.
export interface NrStream {
  readonly addEventListener: (name: string, listener: (e: { readonly data: unknown }) => void) => void;
  readonly close?: () => void;
}

export interface NationalRailOptions {
  readonly map: NrMap;
  readonly berths: ReadonlyMap<string, Area>;
  // Where the stream lives, relative to the page.
  readonly streamUrl: string;
  // A layer id to put the track under (the lowest TfL line layer).
  readonly beforeLayer: string | undefined;
  readonly ink: string;
  // public/nr-track.json (the track model near our berths); without it, the
  // track is drawn from the berth table's recorded step paths.
  readonly track: unknown;
  // Lines hidden in the panel, and the line being hovered there (others dim).
  readonly hidden: () => ReadonlySet<string>;
  readonly focus: () => string | null;
  readonly clock?: () => number;
  readonly connect?: (url: string) => NrStream;
  // parseHandovers of the berth table.
  readonly handovers?: ReturnType<typeof parseHandovers>;
  readonly showElizabeth?: (uid: string | null, date: string | null, headcode: string) => boolean;
}

// public/nr-track.json as GeoJSON, if it is a MultiLineString of [lon, lat]s.
export function parseTrack(data: unknown): GeoJSON.Feature | null {
  const geometry = isRecord(data) ? data['geometry'] : undefined;
  if (!isRecord(geometry) || geometry['type'] !== 'MultiLineString' || !Array.isArray(geometry['coordinates'])) {
    return null;
  }
  const raw: readonly unknown[] = geometry['coordinates'];
  const lines: number[][][] = [];
  for (const line of raw) {
    if (!Array.isArray(line)) return null;
    const pts: readonly unknown[] = line;
    const coords: number[][] = [];
    for (const p of pts) {
      if (!Array.isArray(p)) return null;
      const xy: readonly unknown[] = p;
      const [lon, lat] = xy;
      if (typeof lon !== 'number' || typeof lat !== 'number') return null;
      coords.push([lon, lat]);
    }
    lines.push(coords);
  }
  return { type: 'Feature', properties: {}, geometry: { type: 'MultiLineString', coordinates: lines } };
}

// Tracks as one GeoJSON source: every recorded edge path, each drawn once.
function trackFeatures(berths: ReadonlyMap<string, Area>): GeoJSON.FeatureCollection {
  const seen = new Set<string>();
  const features: GeoJSON.Feature[] = [];
  for (const [areaId, area] of berths) {
    for (const [id, edge] of area.edges) {
      const [a = '', b = ''] = id.split('>');
      const key = areaId + '|' + (a < b ? a + '|' + b : b + '|' + a);
      if (seen.has(key)) continue;
      seen.add(key);
      features.push({
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: edge.path.map(([lat, lon]) => [lon, lat]) },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

const TRACK_LAYER: LayerSpecification = {
  id: 'nr-track',
  type: 'line',
  source: 'nr-track',
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: {
    'line-color': '#A3A9C0',
    'line-opacity': 0.8,
    'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1.2, 12, 2, 15, 3.4],
  },
};

export function createNationalRail(options: NationalRailOptions): NationalRail {
  const { map, berths, ink } = options;
  const clock = options.clock ?? ((): number => performance.now());
  map.addSource('nr-track', { type: 'geojson', data: parseTrack(options.track) ?? trackFeatures(berths) });
  map.addLayer(TRACK_LAYER, options.beforeLayer);

  const trains = new Map<string, NrTrain>();
  let drawnNow: Drawn[] = [];
  // area|berth -> train key.
  const occupied = new Map<string, string>();
  let nextKey = 0;

  // Areas overlap: XA and XB near Victoria report the same berths, EK and VC
  // share EK's 40xx, X2 and X3... Each train is one object whichever areas
  // report it: `occupied` can hold it under several area|berth ids, and it
  // ends when none does.

  function where(key: string, now: number): LatLngTuple | null {
    const t = trains.get(key);
    if (!t?.track) return null;
    const f = t.duration > 0 ? (now - t.start) / t.duration : 1;
    return pointOn(t.track, Math.max(0, Math.min(1, f)));
  }

  // Put a train at a berth's signal, fading in if it wasn't drawn.
  function place(key: string, berth: Berth | undefined): void {
    const t = trains.get(key);
    if (!t) return;
    if (!berth) {
      t.track = null;
      return;
    }
    t.heading = berth.heading ?? null;
    t.track = trackOf([[berth.lat, berth.lon]]);
    t.start = clock();
    t.duration = 0;
  }

  // A new train at a berth.
  function create(area: string, berth: string, descr: string, tag: NrTag | null): string {
    const key = 'nr' + ++nextKey;
    trains.set(key, {
      descr,
      area,
      berth,
      tag,
      track: null,
      start: 0,
      duration: 0,
      moved: clock(),
      alpha: 0,
      dead: false,
      screen: null,
      heading: null,
      lost: null,
      gone: null,
    });
    place(key, berths.get(area)?.berths.get(berth));
    return key;
  }

  // The train on the map that this report is about, if another area (or a
  // report a moment ago) already has it: one another area holds at this
  // same berth id (a berth both report), or else, for a placed berth, the
  // same headcode within TWIN_M, not matched to another timetable uid. A
  // headcode repeats across the network, so a berth with no position only
  // matches the first way.
  function twinOf(area: string, berth: string, descr: string, tag: NrTag | null, now: number): string | null {
    const at = berths.get(area)?.berths.get(berth);
    for (const [id, key] of occupied) {
      if (!id.endsWith('|' + berth) || id.startsWith(area + '|')) continue;
      const t = trains.get(key);
      if (t?.descr !== descr) continue;
      if (
        tag?.lineId === 'elizabeth' &&
        tag.runDate !== undefined &&
        tag.runDate !== null &&
        t.tag?.runDate !== undefined &&
        t.tag.runDate !== null &&
        tag.runDate !== t.tag.runDate
      )
        continue;
      // Not one far off: the same headcode and berth id elsewhere on the
      // network (NK's 2P15 in 0103, SWR's in WI's 0103, 17 km apart). Only
      // distance says so: one area can give a train another's uid (XB
      // tags Thameslink's 2H07 with SWR's).
      const p = at ? where(key, now) : null;
      if (at && p && dist(toLatLon(p), at) > TWIN_M) continue;
      return key;
    }
    if (!at) return null;
    let best: string | null = null;
    let bestD = TWIN_M;
    for (const [key, t] of trains) {
      if (t.descr !== descr) continue;
      if (
        tag?.lineId === 'elizabeth' &&
        tag.runDate !== undefined &&
        tag.runDate !== null &&
        t.tag?.runDate !== undefined &&
        t.tag.runDate !== null &&
        tag.runDate !== t.tag.runDate
      )
        continue;
      const mine = t.tag?.uid ?? null;
      const theirs = tag?.uid ?? null;
      if (mine !== null && theirs !== null && mine !== theirs) continue;
      const p = where(key, now);
      const d = p ? dist(toLatLon(p), at) : Infinity;
      if (d <= bestD) {
        best = key;
        bestD = d;
      }
    }
    return best;
  }

  // Whether the train's home area still has it, and moved it lately: then a
  // report from another area is the same train relayed, and doesn't move it.
  // (Holding on to it longer keeps trains at a home area's wrong copy of a
  // berth: WI's B535 is BS's 0537, 8.6 km off.)
  function relayed(key: string, t: NrTrain, area: string, from: string, to: string, now: number): boolean {
    if (t.area === area || (from !== '' && from === t.berth)) return false;
    let held = false;
    for (const [id, k] of occupied) if (k === key && id.startsWith(t.area + '|')) held = true;
    if (!held) return false;
    const here = berths.get(area);
    const placed = here?.berths.has(to) === true || (from !== '' && here?.through?.has(from + '>' + to) === true);
    // A placed berth takes a train its home has lost in one with no position.
    if (placed && t.lost !== null) return false;
    // A berth with no position never takes a train its home has placed.
    if (!placed && t.lost === null && t.track !== null) return true;
    return now - t.moved < HOME_MS;
  }

  // A matched tag isn't replaced by another area's weaker one.
  const retag = (t: NrTrain, tag: NrTag | null): void => {
    if (tag && (tag.status === 'matched' || t.tag?.status !== 'matched')) t.tag = tag;
  };

  // A train appearing at a berth (interposed, first seen, or in a snapshot):
  // the train already on the map if it's one, else a new one.
  function add(area: string, berth: string, descr: string, tag: NrTag | null): void {
    const now = clock();
    const twin = twinOf(area, berth, descr, tag, now);
    const t = twin === null ? undefined : trains.get(twin);
    if (twin === null || !t) {
      occupied.set(area + '|' + berth, create(area, berth, descr, tag));
      return;
    }
    occupied.set(area + '|' + berth, twin);
    t.dead = false;
    t.gone = null;
    retag(t, tag);
    if (relayed(twin, t, area, '', berth, now)) return;
    moveTo(twin, t, area, t.area === area ? t.berth : '', berth, now);
  }

  // Two objects for one train (each area made its own, and they've met at
  // the same berth id): the older stays, the newer goes.
  function merge(a: string, b: string): string {
    const [keep, drop] = Number(a.slice(2)) < Number(b.slice(2)) ? [a, b] : [b, a];
    for (const [id, k] of occupied) if (k === drop) occupied.set(id, keep);
    // It fades out (where the two agreed, under the one kept).
    const gone = trains.get(drop);
    if (gone) gone.dead = true;
    return keep;
  }

  // An area no longer has this train at this berth. It goes once no area
  // has it anywhere.
  function remove(area: string, berth: string): void {
    const key = occupied.get(area + '|' + berth);
    occupied.delete(area + '|' + berth);
    if (key === undefined) return;
    for (const k of occupied.values()) if (k === key) return;
    const t = trains.get(key);
    if (t) t.gone ??= clock();
  }

  function step(area: string, from: string, to: string, descr: string, tag: NrTag | null): void {
    const key = occupied.get(area + '|' + from);
    const t = key === undefined ? undefined : trains.get(key);
    if (key === undefined || !t) {
      remove(area, to);
      add(area, to, descr, tag);
      return;
    }
    occupied.delete(area + '|' + from);
    if (occupied.get(area + '|' + to) !== key) remove(area, to);
    const now = clock();
    // Another area has this train at this berth too: one train.
    const twin = twinOf(area, to, descr, tag, now);
    const self = twin !== null && twin !== key ? merge(key, twin) : key;
    const train = trains.get(self);
    occupied.set(area + '|' + to, self);
    if (!train) return;
    train.gone = null;
    train.descr = descr;
    retag(train, tag);
    if (relayed(self, train, area, from, to, now)) return;
    moveTo(self, train, area, from, to, now);
  }

  // Moves a train to a berth, from the berth it was at in that area (or ''
  // when it comes from another area).
  function moveTo(key: string, t: NrTrain, area: string, from: string, to: string, now: number): void {
    // From another area: the hand-over's way, if trains are known to go so.
    const across = t.area === area ? undefined : options.handovers?.get(`${t.area}:${t.berth}>${area}:${to}`);
    t.area = area;
    t.berth = to;
    t.moved = now;
    const a = berths.get(area);
    let target = a?.berths.get(to);
    let edge = (from === '' ? undefined : a?.edges.get(from + '>' + to)) ?? across;
    if (!edge && from !== '' && t.tag?.lineId === 'elizabeth') {
      const origin = a?.berths.get(from);
      if (origin && target) {
        for (const other of berths.values()) {
          const candidate = other.edges.get(from + '>' + to);
          const start = other.berths.get(from);
          const end = other.berths.get(to);
          if (
            candidate?.timed === true &&
            start &&
            end &&
            dist(origin, start) <= SAME_M &&
            dist(target, end) <= SAME_M
          ) {
            edge = candidate;
            break;
          }
        }
      }
    }
    const here = where(key, now);
    // Into a berth with no position (a fringe or dummy berth): where trains
    // from here usually go next, keep going there (coasting); otherwise hold
    // where it is for a while (LOST_MS).
    let coasting = false;
    if (!target) {
      const via = from === '' ? undefined : a?.through?.get(from + '>' + to);
      // `to` is a berth here, or another area's as AREA:BERTH (the train
      // leaves this area; that one's report takes it on from there).
      const [toArea, toBerth] = via?.to.includes(':') === true ? via.to.split(':') : [area, via?.to];
      const next = toArea === undefined || toBerth === undefined ? undefined : berths.get(toArea)?.berths.get(toBerth);
      if (!via || !next || !here || !t.track || t.alpha < 0.05) {
        t.lost ??= now;
        return;
      }
      target = next;
      edge = via.edge;
      coasting = true;
    }
    t.lost = null;
    // Not visible where it was (faded out, or never shown): start here.
    if (!here || !t.track || t.alpha < 0.05) {
      place(key, target);
      return;
    }
    // A step can come before the train has reached the berth it's leaving:
    // it carries on along what's left of its track first, instead of
    // cutting across to the new one. It doesn't take the rest of that
    // track's time as well: a train running faster than the recorded times
    // would fall further behind on every step (1G03 drawn 5 km behind).
    const f = t.duration > 0 ? Math.max(0, Math.min(1, (now - t.start) / t.duration)) : 1;
    const rest = restOf(t.track, f);
    const end = rest.at(-1) ?? here;
    const start = edge?.path[0];
    const gap = start ? dist(toLatLon(end), toLatLon(start)) : dist(toLatLon(end), target);
    // Already there (another area's copy of the same berth).
    if (!edge && gap < 1) return;
    // Nowhere near, or no known path and too far to draw straight: fade out
    // here and in at the berth, not a fly-through across the map. The new
    // train takes over wherever this one was held.
    if (gap > REPLACE_M || (!edge && gap > STRAIGHT_MAX_M)) {
      t.dead = true;
      const next = create(area, to, t.descr, t.tag);
      for (const [id, k] of occupied) if (k === key) occupied.set(id, next);
      return;
    }
    const path: LatLngTuple[] = [here, ...rest];
    if (edge) for (const p of edge.path) path.push(p);
    else path.push([target.lat, target.lon]);
    t.track = trackOf(path);
    t.start = now;
    t.duration = Math.max(edge?.seconds ?? DEFAULT_STEP_S, pathMeters(path) / MAX_SPEED) * 1000;
    // Coasting through unplaced berths: lost (and fading LOST_MS later) only
    // if nothing places it by the time it should be there.
    if (coasting) t.lost = now + t.duration;
  }

  function onSnapshot(data: unknown): void {
    const areas = isRecord(data) ? data['areas'] : undefined;
    if (!isRecord(areas)) return;
    const present = new Set<string>();
    for (const [area, a] of Object.entries(areas)) {
      const bs = isRecord(a) ? a['berths'] : undefined;
      if (!isRecord(bs)) continue;
      for (const [berth, b] of Object.entries(bs)) {
        const descr = isRecord(b) ? str(b['descr']) : undefined;
        if (descr === undefined || !isRecord(b)) continue;
        const id = area + '|' + berth;
        present.add(id);
        const key = occupied.get(id);
        const t = key === undefined ? undefined : trains.get(key);
        if (t && t.descr === descr) {
          t.tag = parseTag(b['train']) ?? t.tag;
          continue;
        }
        remove(area, berth);
        add(area, berth, descr, parseTag(b['train']));
      }
    }
    for (const id of occupied.keys()) {
      if (present.has(id)) continue;
      const [area = '', berth = ''] = id.split('|');
      remove(area, berth);
    }
  }

  function onEvent(name: string, data: unknown): void {
    if (!isRecord(data)) return;
    const area = str(data['area']);
    const descr = str(data['descr']);
    if (area === undefined) return;
    const tag = parseTag(data['train']);
    if (name === 'step') {
      const from = str(data['from']);
      const to = str(data['to']);
      if (from === undefined || to === undefined || descr === undefined) return;
      step(area, from, to, descr, tag);
    } else if (name === 'interpose') {
      const berth = str(data['berth']);
      if (berth === undefined || descr === undefined) return;
      remove(area, berth);
      add(area, berth, descr, tag);
    } else if (name === 'cancel') {
      const berth = str(data['berth']);
      if (berth !== undefined) remove(area, berth);
    }
  }

  const source = (options.connect ?? ((url: string): NrStream => new EventSource(url)))(options.streamUrl);
  let lastStreamAt = clock();
  let staleSince: number | null = null;
  let streamCleared = false;
  source.addEventListener('snapshot', (e: { readonly data: unknown }) => {
    lastStreamAt = clock();
    staleSince = null;
    streamCleared = false;
    onSnapshot(parse(e));
  });
  (['step', 'interpose', 'cancel'] as const).forEach((name) => {
    source.addEventListener(name, (e: { readonly data: unknown }) => {
      if (staleSince !== null) return;
      lastStreamAt = clock();
      onEvent(name, parse(e));
    });
  });
  source.addEventListener('heartbeat', () => {
    if (staleSince === null) lastStreamAt = clock();
  });
  source.addEventListener('error', () => {
    staleSince ??= clock();
  });

  function draw(ctx: Canvas2D, dt: number, scale: number, w: number, h: number): void {
    const now = clock();
    if (staleSince === null && now - lastStreamAt > STREAM_SILENCE_MS) staleSince = lastStreamAt + STREAM_SILENCE_MS;
    if (staleSince !== null && !streamCleared && now - staleSince >= STREAM_CLEAR_MS) {
      occupied.clear();
      for (const train of trains.values()) train.dead = true;
      streamCleared = true;
    }
    const streamFresh = staleSince === null;
    const hidden = options.hidden();
    const focus = options.focus();
    drawnNow = [];
    const s = scale;
    const len = 15 * s;
    const wid = 6.4 * s;
    for (const [key, t] of trains) {
      const color = shown(t.tag);
      const lineId = t.tag?.lineId ?? null;
      const p = where(key, now);
      // Fades toward shown or not: gone, lost too long, or not drawable (no
      // position or no known operator) all fade out, and back in once it
      // can be drawn again, never popping in or out.
      if (t.gone !== null && now - t.gone > GRACE_MS) t.dead = true;
      const lostTooLong = t.lost !== null && now - t.lost > LOST_MS;
      const visible = streamFresh && !t.dead && !lostTooLong && color !== null && p !== null;
      if (lineId === 'elizabeth' && options.showElizabeth) {
        const managed = eligible(t, now) && options.showElizabeth(t.tag?.uid ?? null, t.tag?.runDate ?? null, t.descr);
        t.alpha = managed ? 1 : 0;
        if (!managed) {
          t.screen = null;
          if (t.dead) trains.delete(key);
          continue;
        }
      }
      if (dt > 0) t.alpha += ((visible ? 1 : 0) - t.alpha) * Math.min(1, dt / FADE_MS);
      // Not drawable at all: nothing to fade from, so it starts from nothing.
      if (color === null || !p) t.alpha = 0;
      t.screen = null;
      if (t.dead && t.alpha < 0.02) {
        trains.delete(key);
        continue;
      }
      if (color === null || lineId === null || hidden.has(lineId) || !p || t.alpha < 0.02) continue;
      const c = map.project([p[1], p[0]]);
      if (c.x < -30 || c.y < -30 || c.x > w + 30 || c.y > h + 30) continue;
      t.screen = { x: c.x, y: c.y };
      drawnNow.push({ id: key, x: c.x, y: c.y, alpha: t.alpha, label: `${lineId} ${t.descr} ${t.area}:${t.berth}` });
      // Heading from the track just ahead of (or behind) the train.
      let ang: number | null = null;
      if (t.track && t.track.len > 1) {
        const f = t.duration > 0 ? Math.max(0, Math.min(1, (now - t.start) / t.duration)) : 1;
        const ahead = pointOn(t.track, Math.min(1, f + 8 / t.track.len));
        const behind = pointOn(t.track, Math.max(0, f - 8 / t.track.len));
        const q = map.project([ahead[1], ahead[0]]);
        const r = map.project([behind[1], behind[0]]);
        if (Math.hypot(q.x - r.x, q.y - r.y) > 0.01) ang = Math.atan2(q.y - r.y, q.x - r.x);
      }
      if (ang === null && t.heading !== null) {
        // 30 m along the signal's compass heading, projected like the train.
        const rad = t.heading * DEG;
        const q = map.project([
          p[1] + (Math.sin(rad) * 30) / (M_PER_DEG * Math.cos(p[0] * DEG)),
          p[0] + (Math.cos(rad) * 30) / M_PER_DEG,
        ]);
        if (Math.hypot(q.x - c.x, q.y - c.y) > 0.01) ang = Math.atan2(q.y - c.y, q.x - c.x);
      }
      const dim = focus !== null && focus !== lineId ? 0.12 : 1;
      pill(ctx, c.x, c.y, ang, t.alpha * dim, color, ang === null ? wid : len, wid, s, ink);
    }
  }

  function info(key: string): NrTrainInfo | null {
    const t = trains.get(key);
    const p = where(key, clock());
    if (!t || t.dead || !p) return null;
    return { key, descr: t.descr, area: t.area, berth: t.berth, tag: t.tag, lat: p[0], lon: p[1], alpha: t.alpha };
  }

  function eligible(t: NrTrain, at: number): boolean {
    const occupant = occupied.get(t.area + '|' + t.berth);
    return (
      staleSince === null &&
      at - lastStreamAt <= STREAM_SILENCE_MS &&
      !t.dead &&
      t.gone === null &&
      occupant !== undefined &&
      trains.get(occupant) === t &&
      (t.lost === null || at < t.lost) &&
      t.track !== null &&
      shown(t.tag) !== null
    );
  }

  return {
    draw,
    counts: (within) => {
      const n = new Map<string, number>();
      const at = clock();
      for (const [key, t] of trains) {
        const lineId = t.tag?.lineId ?? null;
        if (t.dead || lineId === null || shown(t.tag) === null) continue;
        if (
          lineId === 'elizabeth' &&
          options.showElizabeth &&
          (!eligible(t, at) || !options.showElizabeth(t.tag?.uid ?? null, t.tag?.runDate ?? null, t.descr))
        )
          continue;
        if (within) {
          const p = where(key, at);
          if (!p || !inBox(within, { lat: p[0], lon: p[1] })) continue;
        }
        n.set(lineId, (n.get(lineId) ?? 0) + 1);
      }
      return n;
    },
    pick: (x, y, radius) => {
      let best: string | null = null;
      let bestD = radius;
      for (const [key, t] of trains) {
        // Not one fading out (or not yet in): a train that's gone mustn't
        // hide one nearby.
        if (!t.screen || t.dead || t.alpha < 0.1) continue;
        const d = Math.hypot(t.screen.x - x, t.screen.y - y);
        if (d <= bestD) {
          bestD = d;
          best = key;
        }
      }
      const train = best === null ? null : info(best);
      return train ? { train, distance: bestD } : null;
    },
    info,
    drawn: () => drawnNow,
    byUid: (uid) => {
      for (const [key, t] of trains) {
        if (t.tag?.uid !== uid || shown(t.tag) === null || info(key) === null) continue;
        if (
          t.tag.lineId === 'elizabeth' &&
          options.showElizabeth &&
          (!eligible(t, clock()) || !options.showElizabeth(uid, t.tag.runDate ?? null, t.descr))
        )
          continue;
        return key;
      }
      return null;
    },
    ownsRun: (uid, date, headcode) => {
      for (const t of trains.values()) {
        if (
          t.tag?.lineId === 'elizabeth' &&
          t.tag.uid === uid &&
          t.tag.runDate === date &&
          t.descr === headcode &&
          eligible(t, clock())
        )
          return true;
      }
      return false;
    },
    close: () => {
      source.close?.();
      trains.clear();
      occupied.clear();
      drawnNow = [];
    },
  };
}
